import { FEATURE_ADDON_KEYS } from "../actions/featureAddons";

/**
 * Master switch for addon paywalls and client-side limits.
 * While false, no agency is treated as eligible. The matching server flag is
 * `FEATURE_ADDON_LIMITS_ACTIVE` in flup-firebase-api featureAddons/eligibility.js.
 * Flip both together.
 */
export const FEATURE_ADDON_LIMITS_ACTIVE = true;

/** Plans priced at or below this value (inclusive, any billing period) are eligible. Mirrors the server. */
export const FEATURE_ADDON_MAX_PLAN_PRICE = 99;

export const isFeatureAddonLimitsEligible = ({
  isAppSumo = false,
  isAppSumoResolved = false,
  planPrice = null,
} = {}) => {
  if (FEATURE_ADDON_LIMITS_ACTIVE !== true) return false;
  if (isAppSumoResolved && isAppSumo) return true;
  if (planPrice == null || planPrice === "") return false;
  const price = Number(planPrice);
  return Number.isFinite(price) && price <= FEATURE_ADDON_MAX_PLAN_PRICE;
};

/** Tiered add-ons only. `seats` is a quantity add-on and must stay out of this list. */
export const TIERED_ADDON_KEYS = Object.freeze([
  FEATURE_ADDON_KEYS.AUTOMATIONS,
  FEATURE_ADDON_KEYS.TEMPLATES,
  FEATURE_ADDON_KEYS.COMPETITORS,
]);

export const FEATURE_ADDON_FREE_TIER_ID = "free";
export const FEATURE_ADDON_BILLING_STATUS_UNPAID = "unpaid";

/**
 * Paid tier with an open Stripe subscription that is not active.
 * A cancellation clears `stripeSubscriptionID`, so the free-tier paywalls apply instead.
 * `billingStatus: "unpaid"` is set on renewal failure; older nodes only have the subscription id.
 */
export const isAddonUnpaidRenewal = (agencyAddon) => {
  if (!agencyAddon || agencyAddon.isActive === true) return false;
  const billingStatus = agencyAddon.billingStatus;
  if (billingStatus && billingStatus !== FEATURE_ADDON_BILLING_STATUS_UNPAID) {
    return false;
  }
  const tierId =
    typeof agencyAddon.tierId === "string" ? agencyAddon.tierId.trim() : "";
  if (!tierId || tierId === FEATURE_ADDON_FREE_TIER_ID) return false;
  return (
    typeof agencyAddon.stripeSubscriptionID === "string" &&
    agencyAddon.stripeSubscriptionID.trim().length > 0
  );
};

/** First addon catalog generation. Independent of ads `pricingVersion`. */
export const ADDONS_VERSION_V1 = "v1";
export const CURRENT_ADDONS_VERSION = ADDONS_VERSION_V1;
export const AGENCY_ADDONS_VERSION_RTD_KEY = "addonsVersion";

const SUPPORTED_ADDONS_VERSIONS = Object.freeze([ADDONS_VERSION_V1]);

export const normalizeAddonsVersion = (raw) => {
  if (typeof raw !== "string") return CURRENT_ADDONS_VERSION;
  const key = raw.trim();
  if (SUPPORTED_ADDONS_VERSIONS.includes(key)) return key;
  return CURRENT_ADDONS_VERSION;
};

export const featureAddonCatalogPath = (addonsVersion) =>
  `featureAddons/${normalizeAddonsVersion(addonsVersion)}`;

export const FEATURE_ADDON_COMPETITOR_ACTION = Object.freeze({
  FAVORITE: "favorite",
  GENERATE: "generate",
});

const CAPABILITY_FIELDS = Object.freeze([
  "platforms",
  "frequencies",
  "produceLimit",
  "actions",
  "visibilityLimit",
  "favoriteLimit",
  "generateLimit",
]);

const cloneCapabilityValue = (value) =>
  Array.isArray(value) ? [...value] : value;

const mergeCapabilityActions = (remoteActions, fallbackActions) => {
  if (!Array.isArray(fallbackActions) || fallbackActions.length === 0) {
    return remoteActions;
  }
  const merged = Array.isArray(remoteActions) ? [...remoteActions] : [];
  for (const action of fallbackActions) {
    if (!merged.includes(action)) merged.push(action);
  }
  return merged;
};

const METER_KIND = Object.freeze({
  CAPACITY: "capacity",
  PERIODIC: "periodic",
});

/**
 * Display fallback when `featureAddons/{version}` is missing or unreadable.
 * Paid checkout still uses the server catalog.
 */
const DEFAULT_ADDON_CATALOGS = Object.freeze({
  [FEATURE_ADDON_KEYS.AUTOMATIONS]: Object.freeze({
    meter: METER_KIND.CAPACITY,
    tiers: Object.freeze([
      Object.freeze({
        id: FEATURE_ADDON_FREE_TIER_ID,
        limit: 1,
        priceMonthly: 0,
        platforms: Object.freeze(["instagram"]),
        frequencies: Object.freeze(["3_per_week"]),
      }),
      Object.freeze({
        id: "tier_1",
        limit: 3,
        priceMonthly: 19,
        platforms: Object.freeze(["instagram", "facebook"]),
        frequencies: Object.freeze(["3_per_week", "4_per_week"]),
      }),
      Object.freeze({
        id: "tier_2",
        limit: 10,
        priceMonthly: 49,
        platforms: Object.freeze([
          "instagram",
          "facebook",
          "linkedIn",
          "tiktok",
          "youtube",
        ]),
        frequencies: Object.freeze([
          "3_per_week",
          "4_per_week",
          "5_per_week",
          "6_per_week",
        ]),
      }),
      Object.freeze({
        id: "tier_3",
        limit: 25,
        priceMonthly: 99,
        platforms: Object.freeze([
          "instagram",
          "facebook",
          "linkedIn",
          "tiktok",
          "youtube",
        ]),
        frequencies: Object.freeze([
          "3_per_week",
          "4_per_week",
          "5_per_week",
          "6_per_week",
          "1_per_day",
        ]),
      }),
    ]),
  }),
  [FEATURE_ADDON_KEYS.TEMPLATES]: Object.freeze({
    meter: METER_KIND.CAPACITY,
    tiers: Object.freeze([
      Object.freeze({
        id: FEATURE_ADDON_FREE_TIER_ID,
        limit: 1,
        produceLimit: 10,
        priceMonthly: 0,
      }),
      Object.freeze({
        id: "tier_1",
        limit: 3,
        produceLimit: 30,
        priceMonthly: 19,
      }),
      Object.freeze({
        id: "tier_2",
        limit: 10,
        produceLimit: 70,
        priceMonthly: 49,
      }),
      Object.freeze({
        id: "tier_3",
        limit: 25,
        produceLimit: 150,
        priceMonthly: 99,
      }),
    ]),
  }),
  [FEATURE_ADDON_KEYS.COMPETITORS]: Object.freeze({
    meter: METER_KIND.PERIODIC,
    tiers: Object.freeze([
      Object.freeze({
        id: FEATURE_ADDON_FREE_TIER_ID,
        limit: 1,
        favoriteLimit: 1,
        generateLimit: 1,
        visibilityLimit: 15,
        priceMonthly: 0,
        platforms: Object.freeze(["meta"]),
        actions: Object.freeze([
          FEATURE_ADDON_COMPETITOR_ACTION.FAVORITE,
          FEATURE_ADDON_COMPETITOR_ACTION.GENERATE,
        ]),
      }),
      Object.freeze({
        id: "tier_1",
        limit: 30,
        priceMonthly: 19,
        platforms: Object.freeze(["meta"]),
        actions: Object.freeze([
          FEATURE_ADDON_COMPETITOR_ACTION.FAVORITE,
          FEATURE_ADDON_COMPETITOR_ACTION.GENERATE,
        ]),
      }),
      Object.freeze({
        id: "tier_2",
        limit: 80,
        priceMonthly: 49,
        platforms: Object.freeze(["meta", "google", "linkedIn"]),
        actions: Object.freeze([
          FEATURE_ADDON_COMPETITOR_ACTION.FAVORITE,
          FEATURE_ADDON_COMPETITOR_ACTION.GENERATE,
        ]),
      }),
      Object.freeze({
        id: "tier_3",
        limit: 200,
        priceMonthly: 99,
        platforms: Object.freeze(["meta", "google", "linkedIn"]),
        actions: Object.freeze([
          FEATURE_ADDON_COMPETITOR_ACTION.FAVORITE,
          FEATURE_ADDON_COMPETITOR_ACTION.GENERATE,
        ]),
      }),
    ]),
  }),
});

const tierSortKey = (tierId) => {
  if (tierId === FEATURE_ADDON_FREE_TIER_ID) return -1;
  const match = String(tierId || "").match(/(\d+)$/);
  return match ? Number(match[1]) : 99;
};

const normalizeTiers = (tiers) => {
  const list = Array.isArray(tiers) ? tiers : Object.values(tiers || {});
  return list
    .filter((tier) => tier && tier.id)
    .map((tier) => ({ ...tier }))
    .sort((a, b) => tierSortKey(a.id) - tierSortKey(b.id));
};

const cloneTier = (tier) => {
  const next = { ...tier };
  for (const field of CAPABILITY_FIELDS) {
    if (next[field] != null) {
      next[field] = cloneCapabilityValue(next[field]);
    }
  }
  return next;
};

const mergeTierCapabilities = (tier, fallbackTier) => {
  const next = cloneTier(tier);
  if (!fallbackTier) return next;
  for (const field of CAPABILITY_FIELDS) {
    if (next[field] == null && fallbackTier[field] != null) {
      next[field] = cloneCapabilityValue(fallbackTier[field]);
    }
  }
  if (Array.isArray(fallbackTier.platforms)) {
    next.platforms = [...fallbackTier.platforms];
  }
  if (fallbackTier.produceLimit != null) {
    next.produceLimit = Math.max(
      Number(next.produceLimit) || 0,
      Number(fallbackTier.produceLimit)
    );
  }
  if (fallbackTier.limit != null) {
    next.limit = Math.max(Number(next.limit) || 0, Number(fallbackTier.limit));
  }
  if (Array.isArray(fallbackTier.actions)) {
    next.actions = mergeCapabilityActions(next.actions, fallbackTier.actions);
  }
  if (Array.isArray(fallbackTier.frequencies)) {
    next.frequencies = [...fallbackTier.frequencies];
  }
  return next;
};

export const getDefaultAddonCatalog = (addonKey) => {
  const entry = DEFAULT_ADDON_CATALOGS[addonKey];
  if (!entry) return null;
  return {
    meter: entry.meter,
    tiers: entry.tiers.map((tier) => cloneTier(tier)),
  };
};

export const normalizeAddonCatalogEntry = (entry, addonKey) => {
  const fallback = getDefaultAddonCatalog(addonKey);
  if (!entry || typeof entry !== "object") return fallback;
  const tiers = normalizeTiers(entry.tiers);
  if (!tiers.some((tier) => tier.id === FEATURE_ADDON_FREE_TIER_ID)) {
    return fallback;
  }
  return {
    meter: entry.meter || fallback?.meter,
    tiers: tiers.map((tier) =>
      mergeTierCapabilities(
        tier,
        fallback?.tiers?.find((item) => item.id === tier.id)
      )
    ),
  };
};

export const resolveEffectiveAddonTier = ({ agencyAddon = null, catalog }) => {
  const tiers = catalog?.tiers || [];
  const freeTier = tiers.find((tier) => tier.id === FEATURE_ADDON_FREE_TIER_ID);
  if (!freeTier) return null;

  const isActive = agencyAddon?.isActive === true;
  const requestedTierId =
    isActive && agencyAddon?.tierId
      ? agencyAddon.tierId
      : FEATURE_ADDON_FREE_TIER_ID;
  const paidOrFree = tiers.find((tier) => tier.id === requestedTierId) || null;
  const tier = isActive && paidOrFree ? paidOrFree : freeTier;
  const capabilities = {};
  for (const field of CAPABILITY_FIELDS) {
    if (tier[field] == null) {
      capabilities[field] = null;
      continue;
    }
    capabilities[field] = Array.isArray(tier[field])
      ? [...tier[field]]
      : tier[field];
  }
  if (capabilities.produceLimit != null) {
    capabilities.produceLimit = Number(capabilities.produceLimit);
  }
  if (capabilities.visibilityLimit != null) {
    capabilities.visibilityLimit = Number(capabilities.visibilityLimit);
  }
  if (capabilities.favoriteLimit != null) {
    capabilities.favoriteLimit = Number(capabilities.favoriteLimit);
  }
  if (capabilities.generateLimit != null) {
    capabilities.generateLimit = Number(capabilities.generateLimit);
  }

  return {
    isActive,
    tierId: tier.id,
    limit: Number(tier.limit),
    viewLimit: tier.viewLimit != null ? Number(tier.viewLimit) : null,
    ...capabilities,
  };
};

export const buildFeatureAddonStatus = ({
  addonKey,
  agencyAddons,
  catalogRoot,
  eligible,
}) => {
  const remoteEntry = catalogRoot?.[addonKey];
  const catalog = normalizeAddonCatalogEntry(remoteEntry, addonKey);
  const agencyAddon = agencyAddons?.[addonKey] || null;
  const effective = resolveEffectiveAddonTier({ agencyAddon, catalog });

  return {
    ok: true,
    eligible: Boolean(eligible),
    ungated: !eligible,
    effective,
    tiers: catalog?.tiers || [],
    agencyAddon,
  };
};

/** Second paid tier in catalog order. */
export const getPopularAddonTierId = (tiers = []) => {
  const paid = tiers.filter(
    (tier) => tier?.id && tier.id !== FEATURE_ADDON_FREE_TIER_ID
  );
  return paid[1]?.id || null;
};
